import { Button } from "@/design-system/Button/Button";
import { Check } from "@/design-system/Check/Check";
import { useTranslation } from "@repo/shared/i18n";
import { YEARLY_SAVING_PERCENTAGE } from "@repo/shared/subscription";
import { Oval } from "react-loading-icons";

/*
  The bullets, in render order. The copy itself lives in the subscription
  locale files rather than in `@repo/shared/subscription`, which still holds
  the English originals for mobile — see planCopy.test.ts, which keeps the two
  from drifting apart.
*/
const HEADLINE_BENEFITS = ["unlimitedCredits", "noAds"] as const;

const CLUB_FEATURES = [
  "sbcSolver",
  "myEvolutions",
  "myClub",
  "squadBuilder",
  "recommendations",
  "gallerySolver",
] as const;

type Props = {
  // Absent until /payments/prices answers. Numbers, with the symbol to print
  // them with: the backend decides both, and neither app formats a price.
  monthlyPrice?: number;
  yearlyPrice?: number;
  yearly: boolean;
  currency: string;
  activeSubscription?: boolean;
  /** Checkout (or the customer portal) is being opened. */
  loading?: boolean;
  onGetUltimate: () => void;
};

export const UltimatePlan = ({
  monthlyPrice,
  yearlyPrice,
  yearly,
  currency,
  activeSubscription,
  loading,
  onGetUltimate,
}: Props) => {
  // Yearly leads with the per-month figure *and* names the annual charge, so it
  // needs both; monthly needs only its own. `yearlyPrice` is optional on the
  // wire, and a missing one would print a lone currency symbol.
  const priced = monthlyPrice != null && (!yearly || yearlyPrice != null);
  const { t } = useTranslation("subscription");

  return (
    /*
      One offer, so the panel is a split rather than a card in a row: price and
      commitment on the left, what you get on the right. The CTA sits with the
      price because there is no longer a tier to choose first.
    */
    <section className="mx-auto w-full max-w-[860px] overflow-hidden rounded-2xl border border-primary-blue/45 bg-linear-to-b from-primary-blue/12 to-transparent">
      <div className="grid gap-8 p-7 mobile:grid-cols-[minmax(0,260px)_minmax(0,1fr)] mobile:gap-10 mobile:p-9">
        {/*
          Top-aligned, not centred against the taller feature column: the eye
          starts top-left and reads right, so centring put "Unlimited credits"
          above the plan's own name and left the CTA to be discovered last.
        */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-white">
              {t("tiers.ultimate")}
            </h2>
            {activeSubscription && (
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                {t("ultimatePlan.current")}
              </span>
            )}
          </div>

          {priced && (
            <>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-[44px] font-bold leading-none tabular-nums text-white">
                  {currency}
                  {monthlyPrice}
                </span>
                <span className="text-sm text-foreground-muted">
                  {t("perMonth")}
                </span>
              </div>

              <p className="mt-2.5 text-xs text-dark-tertiary">
                {yearly ? (
                  <>
                    <span>
                      {t("ultimatePlan.billedYearly", {
                        currency: currency,
                        price: Math.round(yearlyPrice),
                      })}
                    </span>
                    <span className="text-primary-blue">
                      {/* {` · save ${YEARLY_SAVING_PERCENTAGE}%`} */}
                      {t("billing.yearlySavePercentage", {
                        percentage: YEARLY_SAVING_PERCENTAGE,
                      })}
                    </span>
                  </>
                ) : (
                  <span>{t("ultimatePlan.billedMonthly")}</span>
                )}
              </p>
            </>
          )}

          <Button
            variant="primary"
            size="large"
            stretch
            className="relative mt-6"
            /*
              The click must not reach document: the modal this opens mounts
              while the click is still propagating, and its outside-click
              handler (useOutsideClick) would read it as a click outside and
              close it in the same gesture.
            */
            onClick={(e) => {
              e.stopPropagation();
              onGetUltimate();
            }}
          >
            <span>
              {activeSubscription
                ? t("ultimatePlan.managePlan")
                : t("ultimatePlan.goUnlimited")}
            </span>
            {loading && (
              <Oval
                width={20}
                height={20}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              />
            )}
          </Button>

          <p className="mt-3 text-xs leading-snug text-dark-tertiary">
            {t("ultimatePlan.continues")}
          </p>
        </div>

        <div className="flex flex-col">
          <ul className="flex flex-col gap-3.5">
            {HEADLINE_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5">
                <Check />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold leading-tight text-white">
                    {t(`ultimatePlan.benefits.${benefit}.title`)}
                  </span>
                  <span className="text-xs leading-snug text-foreground-muted">
                    {t(`ultimatePlan.benefits.${benefit}.info`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          <div className="my-5 h-px bg-neutral-700/70" />

          <p className="mb-3.5 text-[11px] font-medium uppercase tracking-[0.09em] text-dark-tertiary">
            {t("ultimatePlan.withClubImported")}
          </p>

          <ul className="grid gap-3.5 mobile:grid-cols-2">
            {CLUB_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2.5">
                <Check />
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium leading-tight text-white">
                    {t(`ultimatePlan.clubFeatures.${feature}.title`)}
                  </span>
                  <span className="text-xs leading-snug text-foreground-muted">
                    {t(`ultimatePlan.clubFeatures.${feature}.info`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
